import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Film, ArrowLeft, MailCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin: window.location.origin });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white p-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 mb-10" data-testid="forgot-brand">
          <div className="w-8 h-8 rounded-md bg-[#00E5FF] flex items-center justify-center glow-cyan">
            <Film className="w-5 h-5 text-black" />
          </div>
          <span className="font-display text-lg font-semibold tracking-tight">VIRAL3D</span>
        </Link>

        {sent ? (
          <div data-testid="forgot-sent">
            <div className="w-12 h-12 rounded-full bg-[#39FF14]/15 border border-[#39FF14]/30 flex items-center justify-center mb-5">
              <MailCheck className="w-6 h-6 text-[#39FF14]" />
            </div>
            <h1 className="font-display text-3xl font-semibold tracking-tight mb-2">Check your inbox</h1>
            <p className="text-white/50 font-light mb-8 text-sm">
              If an account exists for <span className="text-white/80">{email}</span>, we've sent a reset link. It expires in 60 minutes.
            </p>
            <Link to="/login" className="text-[#00E5FF] hover:underline text-sm flex items-center gap-1" data-testid="forgot-back-login">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-semibold tracking-tight mb-2">Forgot password?</h1>
            <p className="text-white/50 font-light mb-8 text-sm">Enter your email and we'll send you a reset link.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-white/70 text-sm">Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="mt-1.5 bg-zinc-950 border-white/15 h-11 focus-visible:ring-[#00E5FF]"
                  placeholder="you@example.com" data-testid="forgot-email-input" />
              </div>
              <Button type="submit" disabled={loading}
                className="w-full h-11 rounded-full bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium"
                data-testid="forgot-submit-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</> : "Send reset link"}
              </Button>
            </form>
            <p className="mt-6 text-sm text-white/50">
              Remembered it? <Link to="/login" className="text-[#00E5FF] hover:underline" data-testid="forgot-signin-link">Sign in</Link>
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
